import cn from 'classnames'
import { useEffect, useRef, useState } from 'react'
import React from 'react'

import { isMixed } from 'components/uiKit/KitUtils'
import { t } from 'services/Translation'
import { testProps } from 'utils/test/qaData'

import type { IKitControlProps } from '../KitTypes'
import { KitSize } from '../KitTypes'
import * as s from './Input.module.scss'

export type InputDefaultType = Omit<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'onChange' | 'prefix' | 'size' | 'ref' | 'defaultValue' | 'name' | 'onBlur' | 'onFocus' | 'value'
>

export interface IInputProps extends InputDefaultType, IKitControlProps<string> {
  styleType?: 'base' | 'ghost' | 'bordered'
  onChangeEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void
  prefix?: React.ReactNode
  postfix?: React.ReactNode
  el?: 'input' | 'inputNumber'
  parser?: (value: string) => string
  testData?: string
  textAlign?: 'left' | 'right' | 'center'
  width?: React.CSSProperties['width']
  minWidth?: number
  shrink?: React.CSSProperties['flexShrink']
  fluid?: boolean
  /**
   * @formatter USE ONLY FOR DISABLED OR READONLY FIELD
   *  */
  formatter?: (value?: string | null) => string
}

const Input = React.forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      el = 'input',
      name,
      width,
      shrink,
      size = KitSize.M,
      error = false,
      onChange,
      parser,
      formatter,
      prefix,
      postfix,
      onFocus,
      styleType = 'base',
      onBlur,
      defaultValue,
      value: valueProp = defaultValue,
      onChangeEvent,
      testData,
      type = 'text',
      style,
      textAlign,
      minWidth,
      fluid,
      placeholder,
      ...rest
    },
    upperRef,
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null)
    const ref = (upperRef as React.RefObject<HTMLInputElement>) || localRef

    const mixed = isMixed(valueProp)
    const safeValueProp = mixed ? '' : ((valueProp as string | undefined) ?? '')
    const effectivePlaceholder = mixed ? t('input.mixed') : placeholder

    const [focus, setFocus] = useState(false)
    const [value, setValue] = useState(safeValueProp)
    const [pos, setPos] = useState(safeValueProp.length)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsedValue = parser ? parser(e.target.value) : e.target.value
      setPos(e.target.selectionStart || 0)
      setValue(parsedValue)

      if (onChangeEvent) {
        onChangeEvent(e)
      } else {
        onChange?.(parsedValue)
      }
    }

    const rootCn = cn(s.root, s[size], s[styleType], {
      [s.disabled]: rest.disabled,
      [s.focus]: focus,
      [s.error]: error,
      [s.hasPrefix]: !!prefix,
      [s.hasPostfix]: !!postfix,
      [s.fluid]: fluid,
    })

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(false)
      onBlur?.(e)
    }

    useEffect(() => {
      const safeVal = isMixed(valueProp) ? '' : ((valueProp as string | undefined) ?? '')
      setValue(safeVal)
      if (ref.current) {
        ref.current.value = safeVal
        try {
          ref.current.selectionStart = pos
          ref.current.selectionEnd = pos
        } catch {
          // selectionStart/End not supported for some input types (email, number, etc.)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueProp])

    return (
      <div className={rootCn} style={{ width, flexShrink: shrink, minWidth }}>
        {prefix && prefix}
        <input
          {...rest}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={
            rest.onKeyDown
              ? rest.onKeyDown
              : (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                  }
                }
          }
          placeholder={effectivePlaceholder}
          ref={ref}
          style={{ ...style, textAlign }}
          type={type}
          value={(rest.disabled || rest.readOnly) && formatter ? formatter(value) : value}
          {...testProps({
            el,
            name,
            value: el === 'inputNumber' ? parseFloat(value) || null : value,
            ...rest,
            focus,
            testData,
          })}
        />
        {postfix && postfix}
      </div>
    )
  },
)

export default React.memo(Input)
